import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useMutation, useQuery } from '@apollo/client/react'
import { useDispatch, useSelector } from 'react-redux'
import { getFormInitialValues, getFormValues } from 'redux-form'
// hooks
import { useSnackbar } from 'notistack'
import { useStateRef } from 'hooks/useStateRef'
import { useShowModal } from 'hooks/useShowModal'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { InlineFunction, useRiskNotifications } from 'hooks/risks/useRiskNotifications'
import { useLinkedRisksNotifications } from 'hooks/risks/useLinkedRisksNotifications'
// components
import RiskConfirmModal from 'components/Modals/ConfirmRiskModal'
import CheckMandatoryModal from 'components/Modals/RIskMandatoryCheckModal'
// graphql
import { RELATED_RISKS_PERMISSIONS_QUERY } from 'graphql/risk/queries'
import { SYSTEM_PROCESSES_LIST_PB_QUERY, SYSTEM_PROCESSES_LIST_QUERY } from 'graphql/systemProcesses/queries'
import { EXEC_CUSTOM_FUNCTION_MUTATION, EXEC_CUSTOM_FUNCTION_PUBLIC_MUTATION } from 'graphql/risk/mutations'
// constants
import { Forms, Modals } from 'constants/index'
// types
import {
  FieldType,
  IField,
  IPermissionsMap,
  IRiskTemplate,
  ISystemProcess,
  ISystemProcessCondition,
  ISystemProcessTransaction,
  SystemProcessConditionAction,
  SystemProcessObject,
  SystemProcessTransactionType
} from 'types/graphql'
// redux
import { setPublicFormData } from 'store/reducers/publicRisk'
import { addRelatedForm, removeRelatedForm } from 'store/reducers/relatedForms'
// context
import { PublicContext } from 'containers/Risks/Risk/Edit/public'
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// utils
import moment from 'moment'
import { handleError } from 'utils/errors/handlers'
import { checkIsFieldValueExist } from './utils'
import { getMatrixSelectedOptionId } from 'utils/form/matrix'
import { pipe, sortBy as sortByFp, uniqBy } from 'lodash/fp'
import { get, isEqual, isFunction, isNil, sortBy } from 'lodash'

type Props = React.PropsWithChildren<unknown>

const TYPE_PRIORITY = {
  [SystemProcessTransactionType.CheckMandatory]: 1,
  [SystemProcessTransactionType.Confirmation]: 2,
  [SystemProcessTransactionType.RemoveGuestAccess]: 4
}

type SystemProcessFn = { name: string; createdAt: string }

interface Context {
  spFunctions: SystemProcessFn[]
  inlineFunctions: InlineFunction[]
  onProcessFnClick: (fnName: string, closePopup?: () => void) => () => Promise<void>
}

export const RiskTransactionsContext = React.createContext<Context>({} as any)

const RiskTransactions: React.FC<Props> = ({ children }) => {
  const user = useCurrentUser()
  const router = useRouter()
  const dispatch = useDispatch()

  const isPublic = React.useContext(PublicContext)

  const { risk } = React.useContext(RiskAPIContext)
  const { riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const { data: systemProcessesQuery, loading } = useQuery(
    isPublic ? SYSTEM_PROCESSES_LIST_PB_QUERY : SYSTEM_PROCESSES_LIST_QUERY,
    {
      fetchPolicy: 'network-only',
      variables: {
        filter: {
          objectId: router.query.templateId,
          objects: [SystemProcessObject.RiskTemplate]
        },
        pagination: {
          pageTake: 1000,
          pageSkip: 0
        },
        ...(isPublic && { domain: router.query.domain })
      }
    }
  )

  const skipPermissionsQuery = isPublic || !router.query.id

  const {
    data: relatedRisksPermissionsQuery,
    loading: permissionsLoading,
    refetch: refetchRelatedRisksPermissions
  } = useQuery(RELATED_RISKS_PERMISSIONS_QUERY, {
    fetchPolicy: 'network-only',
    skip: skipPermissionsQuery,
    variables: {
      riskId: router.query.id,
      riskTemplateId: router.query.templateId
    }
  })

  const [executeCustomFunctionMutation] = useMutation(
    isPublic ? EXEC_CUSTOM_FUNCTION_PUBLIC_MUTATION : EXEC_CUSTOM_FUNCTION_MUTATION
  )

  const { enqueueSnackbar } = useSnackbar()

  const { showModal } = useShowModal({ modalType: Modals.RISK_CONFIRM_MODAL })
  const { showModal: showMandatoryCheck } = useShowModal({ modalType: Modals.RISK_MANDATORY_CHECK_MODAL })

  const [spFunctions, setSpFunctions, spFunctionsRef] = useStateRef<SystemProcessFn[]>([])

  const formValues = useSelector((state: any) => getFormValues(Forms.RISK_TEMPLATE_FORM)(state))
  const initialValues = useSelector((state: any) => getFormInitialValues(Forms.RISK_TEMPLATE_FORM)(state))

  const relatedForms = useSelector((state: any) => state.relatedForms[risk?.id] || [])

  const formValuesRef = React.useRef(null)

  const onRefetchRelatedForms = () => {
    if (!skipPermissionsQuery) {
      refetchRelatedRisksPermissions()
    }
  }

  const { inlineFunctions } = useRiskNotifications({
    riskId: risk.id,
    riskTemplateId: riskTemplate.id,
    fieldLinks: riskTemplate.fieldLinks,
    onRefetchRelatedForms
  })

  useLinkedRisksNotifications(riskTemplate.fieldLinks)

  // constants
  const riskTemplates: IRiskTemplate[] = get(
    relatedRisksPermissionsQuery,
    'getRelatedFormsPermissions.riskTemplates',
    []
  )

  const isMultipleRelatedRisk = !!router.query.multiple
  const relatedFormsPermissions: IPermissionsMap[] = get(
    relatedRisksPermissionsQuery,
    'getRelatedFormsPermissions.permissionsMap',
    []
  )

  const systemProcesses: ISystemProcess[] = React.useMemo(
    () =>
      get(systemProcessesQuery, `${isPublic ? 'systemProcessesPublic' : 'systemProcesses'}.data`, []).filter(
        ({ active }) => active
      ),
    [loading]
  )

  const spWithCondition: ISystemProcess[] = React.useMemo(
    () => systemProcesses.filter(({ conditions }) => !!conditions.length),
    [systemProcesses.length]
  )

  const spWithoutCondition: ISystemProcess[] = React.useMemo(
    () => systemProcesses.filter(({ conditions }) => !conditions.length),
    [systemProcesses.length]
  )

  // methods
  const executeCustomFunction = async (processId: string) => {
    try {
      await executeCustomFunctionMutation({
        variables: {
          input: {
            riskId: risk.id,
            processId
          },
          ...(isPublic && { domain: router.query.domain })
        }
      })
    } catch (err) {
      handleError(err, 'RiskTransactions.executeCustomFunction')
    }
  }

  const removeGuestAccess = async (successText: string) => {
    dispatch(setPublicFormData({ confirmed: true, successText }))
  }

  const executeTransaction = async ({
    process,
    transaction
  }: {
    process: ISystemProcess
    transaction: ISystemProcessTransaction
  }) => {
    if (isMultipleRelatedRisk) {
      return false
    }

    if (
      transaction.transactionType === SystemProcessTransactionType.ShowRelatedForm &&
      !relatedForms.some(({ riskTemplateId }) => riskTemplateId === transaction.relatedForms.riskTemplateId)
    ) {
      const relatedFormPermissions = relatedFormsPermissions.find(
        ({ riskTemplateId }) => riskTemplateId === transaction.relatedForms.riskTemplateId
      )

      const relatedTemplate = riskTemplates.find(({ id }) => id === transaction.relatedForms.riskTemplateId)

      if (relatedFormPermissions) {
        const relatedForm = {
          ...transaction.relatedForms,
          ...relatedFormPermissions,
          riskTemplate: relatedTemplate
        }

        dispatch(
          addRelatedForm({
            id: risk.id,
            form: relatedForm
          })
        )
      }
    }

    if (
      transaction.transactionType === SystemProcessTransactionType.ShowFunction &&
      spFunctionsRef.current.every((fn) => fn.name !== transaction.functionName)
    ) {
      const properties = ['availableToPositionIds', 'availableToUserIds', 'availableToFieldIds']
      const shouldCheckId = properties.some((prop) => transaction[prop]?.length)

      if (
        shouldCheckId
          ? user?.userAndPositionIds?.some(
              (id) =>
                transaction.availableToPositionIds?.includes(id) ||
                transaction.availableToUserIds?.includes(id) ||
                transaction.availableToFieldIds?.some((fieldId) => formValues[fieldId]?.value?.value === id)
            )
          : true
      ) {
        setSpFunctions((prev) =>
          pipe(
            uniqBy<SystemProcessFn>('name'),
            sortByFp<SystemProcessFn>('createdAt')
          )([...prev, { createdAt: process.createdAt, name: transaction.functionName }])
        )
      }
    }

    if (transaction.transactionType === SystemProcessTransactionType.ResponseMessage) {
      enqueueSnackbar(transaction.template, {
        variant: 'info',
        autoHideDuration: 8000
      })
    }

    if (transaction.transactionType === SystemProcessTransactionType.Confirmation) {
      return (await new Promise((resolve) => {
        showModal({ text: transaction.template, title: transaction.subject, resolve })
      })) as boolean
    }

    const isParentHidden = (field: IField) => {
      const parent = riskTemplate.fields.find((i) => i.id === field.parentFieldId)

      if (parent) {
        return parent.parentFieldId ? isParentHidden(parent) : !parent.hidden
      }

      return false
    }

    if (transaction.transactionType === SystemProcessTransactionType.CheckMandatory) {
      const requiredFields = riskTemplate.fields.filter(
        (field) => field.required && !field.hidden && (field.parentFieldId ? isParentHidden(field) : true)
      )

      const showCheck = requiredFields.some((field: IField & { fieldLinkId: string }) => {
        if (field.fieldLinkId) {
          return checkIsFieldValueExist(formValues, field.fieldLinkId, field.fieldType)
        }

        const fieldValue = formValues[field.id]?.value

        if (isNil(fieldValue)) {
          return true
        }

        if (Array.isArray(fieldValue)) {
          return !fieldValue.length
        }

        if (moment.isMoment(fieldValue)) {
          return !fieldValue.isValid()
        }

        if (field.fieldType === FieldType.DocumentOrLink) {
          return !fieldValue.url
        }

        if (typeof fieldValue === 'object') {
          return !fieldValue.value
        }

        return !fieldValue
      })

      if (showCheck) {
        showMandatoryCheck({ text: transaction.template, title: transaction.subject })
        return false
      }
    }

    if (transaction.transactionType === SystemProcessTransactionType.RemoveGuestAccess && isPublic) {
      await removeGuestAccess(transaction.subject)
    }

    return true
  }

  const cancelTransaction = (transaction: ISystemProcessTransaction) => {
    if (transaction.transactionType === SystemProcessTransactionType.ShowFunction) {
      if (spFunctionsRef.current.some((fn) => fn.name === transaction.functionName)) {
        setSpFunctions((prev) => prev.filter((fn) => fn.name !== transaction.functionName))
      }
    }

    if (
      transaction.transactionType === SystemProcessTransactionType.ShowRelatedForm &&
      relatedForms.some(({ riskTemplateId }) => riskTemplateId === transaction.relatedForms.riskTemplateId)
    ) {
      dispatch(removeRelatedForm({ riskId: risk.id, id: transaction.relatedForms.riskTemplateId }))
    }
  }

  const processTransactions = async ({
    fnName,
    processes,
    shouldCancel,
    skipConditions
  }: {
    fnName?: string
    processes: ISystemProcess[]
    shouldCancel?: boolean
    skipConditions?: boolean
  }) => {
    for (const process of processes) {
      const { conditions, transactions } = process

      let shouldContinueEx = true

      const shouldExecute = conditions.some((orCondition) =>
        orCondition.conditions.every((andCondition) => checkCondition(andCondition, fnName))
      )

      if (shouldExecute || skipConditions) {
        const sortedTransactions = sortBy(transactions, (item) => TYPE_PRIORITY[item.transactionType] || 3)
        let customFunctionExecuted = false

        for (const transaction of sortedTransactions) {
          let shouldContinue = true

          if (
            fnName &&
            !isMultipleRelatedRisk &&
            !customFunctionExecuted &&
            [SystemProcessTransactionType.SetField, SystemProcessTransactionType.SendEmail].includes(
              transaction.transactionType
            )
          ) {
            customFunctionExecuted = true
            await executeCustomFunction(process.id)
          }

          shouldContinue = await executeTransaction({
            process,
            transaction
          })

          if (!shouldContinue) {
            shouldContinueEx = false
            break
          }
        }

        if (!shouldContinueEx) {
          break
        }
      } else if (shouldCancel) {
        transactions.forEach(cancelTransaction)
      }
    }

    if (!skipConditions) {
      formValuesRef.current = formValues
    }
  }

  const getConditionField = (condition: ISystemProcessCondition): IField =>
    riskTemplate?.fields?.find(({ id }) => id === condition.fieldName) ||
    riskTemplate?.fieldLinks?.find(({ field }) => field?.id === condition.fieldName)?.field

  const checkCondition = (condition: ISystemProcessCondition, fnName?: string) => {
    if (condition.fieldName === 'customFunction') {
      return fnName && condition.value?.value === fnName
    }

    const isArray = Array.isArray(get(formValues?.[condition.fieldName], 'value'))
    const fieldValue = formValues?.[condition.fieldName]?.value

    // the matrix value is a cell id, while the condition holds a matrix option id
    if (
      condition.fieldType === FieldType.RiskMatrix &&
      [SystemProcessConditionAction.Is, SystemProcessConditionAction.IsNot].includes(condition.action)
    ) {
      const optionId = getMatrixSelectedOptionId(fieldValue, getConditionField(condition))

      return condition.action === SystemProcessConditionAction.Is
        ? optionId === condition.value?.value
        : optionId !== condition.value?.value
    }

    if (condition.action === SystemProcessConditionAction.Is) {
      if (condition.fieldType === FieldType.PickList) {
        if (isArray) {
          return fieldValue?.some(({ value }) => value === condition.value.value)
        }
        return fieldValue?.value === condition.value.value
      }
      return fieldValue === condition.value.value
    }

    if (condition.action === SystemProcessConditionAction.IsNot) {
      if (condition.fieldType === FieldType.PickList) {
        if (isArray) {
          return fieldValue?.every(({ value }) => value !== condition.value.value)
        }
        return fieldValue?.value !== condition.value.value
      }
      return fieldValue !== condition.value.value
    }

    if (condition.action === SystemProcessConditionAction.HasChanged) {
      return !isEqual(formValues[condition.fieldName], formValuesRef.current?.[condition.fieldName])
    }
  }

  const onProcessFnClick: Context['onProcessFnClick'] = (fnName, closePopup) => async () => {
    const currentFnProcesses = spWithCondition.filter(({ conditions }) =>
      conditions.some((orCondition) =>
        orCondition.conditions.some(
          (andCondition) => andCondition.fieldName === 'customFunction' && andCondition.value?.value === fnName
        )
      )
    )

    await processTransactions({ processes: currentFnProcesses, fnName })

    isFunction(closePopup) && closePopup()
  }

  // effects
  React.useEffect(() => {
    if (initialValues) {
      formValuesRef.current = initialValues
    }
  }, [initialValues])

  React.useEffect(() => {
    if (spFunctions.length > 0 || isMultipleRelatedRisk) {
      setSpFunctions([])
    }
  }, [risk.id, router.query.id])

  React.useEffect(() => {
    if (!formValues || !systemProcesses.length || !risk.id || permissionsLoading) {
      return
    }

    if (spWithCondition.length) {
      processTransactions({ processes: spWithCondition, shouldCancel: true })
    }

    if (spWithoutCondition.length) {
      processTransactions({ processes: spWithoutCondition, skipConditions: true })
    }
  }, [JSON.stringify(formValues), systemProcesses.length, risk.id, permissionsLoading, relatedRisksPermissionsQuery])

  const value = React.useMemo(
    () => ({ spFunctions, inlineFunctions, onProcessFnClick }),
    [spFunctions, onProcessFnClick]
  )

  return (
    <RiskTransactionsContext.Provider value={value}>
      {children}
      <RiskConfirmModal />
      <CheckMandatoryModal />
    </RiskTransactionsContext.Provider>
  )
}

export default RiskTransactions
